import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import type { Dayjs } from 'dayjs';
import { activeFilterCount, dateRangeParams } from '@/domain/filters';

/** 四个列表页共用的筛选状态。所有字段可选，空对象就是「不筛」。 */
export interface BaseFilter {
  keyword?: string;
  createdFrom?: string;
  createdTo?: string;
}

/**
 * 列表筛选的状态机。
 *
 * 把四个页面都要写一遍的那几件事收在一处：输入框防抖、空数组归零、时间区间转参数、
 * 一键清空、以及「现在到底筛了几个条件」。
 *
 * 关键词单独存一份本地值：输入框要立刻回显用户敲的字，但请求得等他停手 —— 每敲一个字母
 * 打一次接口，在打款记录这种表上就是几十次全表扫描。
 */
export function useListFilter<T extends BaseFilter>(debounceMs = 300) {
  const [filter, setFilter] = useState<T>({} as T);
  const [keyword, setKeywordInput] = useState('');
  const [range, setRangeState] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const pushKeyword = useMemo(
    () =>
      debounce((value: string) => {
        setFilter((f) => ({ ...f, keyword: value.trim() || undefined }));
      }, debounceMs),
    [debounceMs],
  );

  // 组件卸载时取消挂起的那一次，否则会在卸载后 setState
  useEffect(() => () => pushKeyword.cancel(), [pushKeyword]);

  const setKeyword = useCallback(
    (value: string) => {
      setKeywordInput(value);
      pushKeyword(value);
    },
    [pushKeyword],
  );

  /**
   * 改一个筛选字段。
   * 🔴 空数组一律归成 undefined：把最后一个勾去掉时用户要的是「全都要」，
   * 而不是「一个都不要」。后端对空数组也是这么理解的，两边必须一致。
   */
  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFilter((f) => ({
      ...f,
      [key]: Array.isArray(value) && value.length === 0 ? undefined : value,
    }));
  }, []);

  /** 一次改多个字段（金额区间那样成对出现的）。身份稳定，可以安全地进依赖数组。 */
  const setMany = useCallback((patch: Partial<T>) => {
    setFilter((f) => ({ ...f, ...patch }));
  }, []);

  const setRange = useCallback((next: [Dayjs | null, Dayjs | null] | null) => {
    setRangeState(next);
    setFilter((f) => ({ ...f, ...dateRangeParams(next) }));
  }, []);

  const reset = useCallback(() => {
    pushKeyword.cancel();
    setKeywordInput('');
    setRangeState(null);
    setFilter({} as T);
  }, [pushKeyword]);

  return {
    filter,
    /** 输入框的即时值（未防抖）。 */
    keyword,
    setKeyword,
    set,
    setMany,
    /** RangePicker 的即时值。 */
    range,
    setRange,
    reset,
    active: activeFilterCount(filter),
  };
}
